import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Card, CardContent } from "../ui/card";
import { LoadingSpinner } from '../ui/loading-spinner';
import RevenueGraph from './RevenueGraph';
import { reportService } from '../../services/reportService';
import { getAuth } from 'firebase/auth';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { subscriptionService } from '../../services/subscriptionService';
import { reportCacheUtil } from '../../helpers/reportCacheUtil';
import { Button } from '../ui/button';
import { RefreshCw } from 'lucide-react';
import { getCurrentTaxYear, getTaxYearBoundaries, formatTaxYear } from '../../helpers/taxYearUtil';
import HelpIcon from '../ui/HelpIcon'; // Import HelpIcon
import { useTheme } from '../ui/theme-provider'; // Import useTheme
import { cn } from '../../lib/utils'; // Import cn

const Reports = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [viewMode, setViewMode] = useState('treatmentDate');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('6');
  const [practice, setPractice] = useState('all');
  const [practices, setPractices] = useState([]); // Keep practices state
  const [availablePeriods, setAvailablePeriods] = useState(['1', '3', '6']);
  const [availableYears, setAvailableYears] = useState([]);
  const [customTaxYear, setCustomTaxYear] = useState(null);
  const [dataCache, setDataCache] = useState({});
  const [baseDataTimestamp, setBaseDataTimestamp] = useState(null);
  const [defaultStartDate, setDefaultStartDate] = useState(null); // State to hold the default start date
  const auth = getAuth();
  const navigate = useNavigate();
  const { subscription } = useOutletContext() || {};
  const [activeChartTab, setActiveChartTab] = useState('monthlyRevenue');
  const { theme } = useTheme(); // Get theme

  const getMonthIndex = (monthStr) => {
    const monthPart = monthStr.split(' ')[0];
    const fullMonthMap = {
      'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
      'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
    };
    const shortMonthMap = {
      'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
      'jul': 6, 'aug': 7, 'sep': 8, 'sept': 8, 'oct': 9, 'nov': 10, 'dec': 11
    };
    const lowerMonth = monthPart.toLowerCase();
    if (fullMonthMap[lowerMonth] !== undefined) {
      return fullMonthMap[lowerMonth];
    }
    if (shortMonthMap[lowerMonth] !== undefined) {
      return shortMonthMap[lowerMonth];
    }
    if (!isNaN(parseInt(lowerMonth, 10))) {
      return parseInt(lowerMonth, 10) - 1;
    }
    for (const [month, index] of Object.entries(fullMonthMap)) {
      if (month.startsWith(lowerMonth)) {
        return index;
      }
    }
    return 0;
  };

  const resetFilters = () => {
    setFilter('6');
    setAvailablePeriods(['1', '3', '6']);
  };

  // Calculate default start date (start of previous tax year)
  useEffect(() => {
    const currentTaxYear = getCurrentTaxYear();
    const prevTaxYear = currentTaxYear - 1;
    const { start } = getTaxYearBoundaries(prevTaxYear);
    setDefaultStartDate(start);
    console.log(`Reports: Default start date set to ${start.toISOString()}`);
  }, []); // Run only once on mount

  // Updated checkAvailablePeriods to use the optimized service function
  const checkAvailablePeriods = useCallback(async () => { // Wrap in useCallback
    if (!auth.currentUser) return;
    try {
      // Use the new optimized function
      const oldestTimestamp = await reportService.getOldestInvoiceTimestamp(auth.currentUser.uid);
      const now = new Date();
      let oldestDate = oldestTimestamp ? oldestTimestamp.toDate() : now; // Use fetched timestamp or now if none

      const monthsDiff = Math.ceil(
        (now.getTime() - oldestDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44)
      );
      
      // Determine available periods based on oldest date
      const periods = ['0', '1', '3', '6']; // Add '0' for "This Month"
      if (monthsDiff > 6) periods.push('12');
      if (monthsDiff > 12) periods.push('24');
      periods.push('tax-current');
      periods.push('tax-previous');
      periods.push('all-time'); // Keep all-time option

      // Determine available tax years based on oldest date
      const currentTaxYear = getCurrentTaxYear();
      let availableYearsList = [currentTaxYear, currentTaxYear - 1];
      const startOfPrevTaxYear = getTaxYearBoundaries(currentTaxYear - 1).start;
      
      if (oldestDate < startOfPrevTaxYear) {
        // Add more years if data exists before the previous tax year
        let yearToCheck = currentTaxYear - 2;
        while (getTaxYearBoundaries(yearToCheck).start > oldestDate) {
          availableYearsList.push(yearToCheck);
          yearToCheck--;
        }
        // Add the year containing the oldest date
        availableYearsList.push(yearToCheck); 
      }

      availableYearsList = [...new Set(availableYearsList)].sort((a, b) => b - a);
      
      setAvailablePeriods(periods);
      setAvailableYears(availableYearsList);

    } catch (error) {
      console.error('Error calculating periods:', error);
      // Fallback values
      setAvailablePeriods(['0', '1', '3', '6', 'tax-current', 'tax-previous', 'all-time']); // Add '0' here too
      const currentTaxYear = getCurrentTaxYear();
      setAvailableYears([currentTaxYear, currentTaxYear - 1]);
    }
  }, [auth.currentUser]); // Dependency: auth.currentUser

  const processAndSortData = (data) => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    return data.sort((a, b) => {
      const aMonth = a.month?.split(' ')[0] || '';
      const aYear = parseInt(a.month?.split(' ')[1]) || 0;
      const bMonth = b.month?.split(' ')[0] || '';
      const bYear = parseInt(b.month?.split(' ')[1]) || 0;
      if (aYear !== bYear) {
        return bYear - aYear;
      }
      const monthA = getMonthIndex(aMonth);
      const monthB = getMonthIndex(bMonth);
      return monthB - monthA;
    });
  };

  // Modify fetchData to accept a practice parameter
  const fetchData = useCallback(async (practiceOverride = null) => { // Wrap in useCallback
    if (!auth.currentUser || !defaultStartDate) { // Ensure defaultStartDate is calculated
        console.log("FetchData: Aborting - No user or defaultStartDate not ready.");
        return;
    }

    const practiceValue = practiceOverride !== null ? practiceOverride : practice;
    
    // Determine the start date for the query
    // If filter is 'all-time', startDateForQuery is null (fetch all)
    // Otherwise, use the defaultStartDate
    const startDateForQuery = filter === 'all-time' ? null : defaultStartDate;
    
    // Include startDateForQuery in the cache key logic
    const cacheKey = `${viewMode}-${filter}-${practiceValue}-${startDateForQuery ? startDateForQuery.toISOString() : 'full'}`;

    if (dataCache[cacheKey]) {
      // If using cache, ensure practices are derived from cached data
      const cachedResult = dataCache[cacheKey];
      const uniquePractices = getUniquePracticesFromResult(cachedResult); // Use helper
      setPractices(uniquePractices);
      setData(cachedResult);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      let result;
      let baseInvoicesForPracticeDerivation = []; // Store invoices used for practice list

      if (filter.startsWith('tax-')) {
        // Fetch base data for tax year filtering
        const baseInvoices = await reportService.getAllInvoiceData(auth.currentUser.uid, practiceValue, startDateForQuery);
        baseInvoicesForPracticeDerivation = baseInvoices; // Use these for practice list if practiceValue is 'all'
        const activeInvoices = baseInvoices.filter(inv => inv.status !== 'deleted');

        // Client-side filtering for the specific tax year
        let taxYearStartYear;
        if (filter === 'tax-custom' && customTaxYear) {
          taxYearStartYear = customTaxYear;
        } else if (filter === 'tax-previous') {
          taxYearStartYear = getCurrentTaxYear() - 1;
        } else { // tax-current
          taxYearStartYear = getCurrentTaxYear();
        }
        const { start, end } = getTaxYearBoundaries(taxYearStartYear);

        // Filter the already fetched (potentially limited) data
        let filteredInvoices;
        if (viewMode === 'treatmentDate') {
          // FIXED: Filter at entry level, not invoice level to match tax service logic
          filteredInvoices = activeInvoices.map(invoice => {
            if (!invoice.summaryData || !Array.isArray(invoice.summaryData)) return null;
            
            // Filter summaryData to only include entries within the tax year
            const filteredSummaryData = invoice.summaryData.filter(entry => {
              let treatmentDate;
              if (entry.date?.seconds) treatmentDate = new Date(entry.date.seconds * 1000);
              else if (entry.date?.toDate) treatmentDate = entry.date.toDate();
              else if (entry.date instanceof Date) treatmentDate = entry.date;
              else if (typeof entry.date === 'string') treatmentDate = new Date(entry.date);
              
              if (!treatmentDate || isNaN(treatmentDate.getTime())) return false;
              return treatmentDate >= start && treatmentDate <= end;
            });
            
            // Only include invoice if it has entries within the tax year
            if (filteredSummaryData.length > 0) {
              return {
                ...invoice,
                summaryData: filteredSummaryData // Replace with filtered entries only
              };
            }
            return null;
          }).filter(invoice => invoice !== null);
        } else { // invoiceDate
          filteredInvoices = activeInvoices.filter(invoice => {
            if (!invoice.createdAt?.seconds) return false;
            const invoiceDate = new Date(invoice.createdAt.seconds * 1000);
            return invoiceDate >= start && invoiceDate <= end;
          });
        }
        result = reportService.calculateMonthlyAmounts(filteredInvoices, viewMode);

      } else { 
        // Handles numeric filters ('0', '1', '3', '6', '12', '24') and 'all-time'
        const months = filter === 'all-time' ? 'all' : (filter === '0' ? 1 : parseInt(filter)); // Use 1 month fetch for 'This Month' filter
        
        // Fetch data using appropriate service method
        // We need the underlying invoice data to derive practices if practiceValue is 'all'
        if (practiceValue === 'all') {
           // Fetch raw data first to get practices
           const rawData = viewMode === 'treatmentDate'
             ? await reportService.getAllTreatmentData(auth.currentUser.uid, practiceValue, startDateForQuery)
             : await reportService.getAllInvoiceData(auth.currentUser.uid, practiceValue, startDateForQuery);
           baseInvoicesForPracticeDerivation = rawData; // Use this raw data

           // Now calculate the monthly amounts based on the raw data and months filter
           result = viewMode === 'treatmentDate'
             ? await reportService.getRevenueByTreatmentDate(auth.currentUser.uid, months, practiceValue, startDateForQuery) // This might re-fetch, consider optimizing service if needed
             : await reportService.getRevenueByInvoiceDate(auth.currentUser.uid, months, practiceValue, startDateForQuery); // This might re-fetch
        } else {
           // If a specific practice is selected, we don't need to derive the list here
           result = viewMode === 'treatmentDate'
             ? await reportService.getRevenueByTreatmentDate(auth.currentUser.uid, months, practiceValue, startDateForQuery)
             : await reportService.getRevenueByInvoiceDate(auth.currentUser.uid, months, practiceValue, startDateForQuery);
           // Set practices to only the selected one if needed, or leave as is? Let's update based on result below.
        }
      }

      // --- Filter result for "This Month" if filter === '0' ---
      let finalResult = result;
      if (filter === '0') {
        const now = new Date();
        const currentMonth = now.toLocaleString('default', { month: 'long' });
        const currentYear = now.getFullYear();
        const currentMonthStr = `${currentMonth} ${currentYear}`;
        finalResult = result.filter(item => item.month === currentMonthStr);
        // If the current month isn't in the result (e.g., no data yet), finalResult will be empty.
      }
      // --- End "This Month" Filter ---


      // --- Derive Practices from the result or baseInvoices ---
      let uniquePractices;
      if (practiceValue === 'all') {
         // If 'All Practices' is selected, derive from the base invoices fetched for this view
         uniquePractices = getUniquePracticesFromInvoices(baseInvoicesForPracticeDerivation);
      } else {
         // If a specific practice was selected, the list should contain only that practice (if it exists in the result)
         // Or potentially keep the broader list? Let's derive from the result to be accurate to the view.
         uniquePractices = getUniquePracticesFromResult(finalResult); // Derive from potentially filtered result
         // Ensure the currently selected practice is still in the list, if not, reset?
         // For simplicity, let's just show practices present in the current result set.
      }
      setPractices(uniquePractices);
      // --- End Practice Derivation ---


      const sortedResult = processAndSortData(finalResult); // Process the potentially filtered result
      setDataCache(prev => ({
        ...prev,
        [cacheKey]: sortedResult
      }));
      setData(sortedResult);
      setBaseDataTimestamp(Date.now());

      // If the currently selected practice is no longer in the derived list, reset to 'all'
      if (practiceValue !== 'all' && !uniquePractices.some(p => p.id === practiceValue)) {
         console.log(`Reports: Current practice ${practiceValue} not found in new data set. Resetting to 'all'.`);
         setPractice('all'); // Resetting might trigger another fetch, consider if this is desired.
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
      setPractices([]); // Clear practices on error
    }
    setLoading(false);
  }, [
      auth.currentUser,
      viewMode,
      filter,
      practice, // Keep practice as dependency
      customTaxYear,
      dataCache,
      defaultStartDate
  ]);

  // Helper function to get unique practices from calculated monthly results
  const getUniquePracticesFromResult = (resultData) => {
     if (!resultData) return [];
     const practicesMap = new Map();
     resultData.forEach(monthData => {
       monthData.invoiceDetails?.forEach(invDetail => {
         if (invDetail.practiceName && invDetail.practiceName !== 'Unknown' && invDetail.id) {
            // Need practice ID. Assuming invoiceDetails has practiceId or we infer it?
            // Let's assume calculateMonthlyAmounts includes practiceId in invoiceDetails
            // If not, this needs adjustment or use getUniquePracticesFromInvoices
            const practiceId = invDetail.practiceId; // *** Requires practiceId in invoiceDetails ***
            if (practiceId && !practicesMap.has(practiceId)) {
               practicesMap.set(practiceId, { id: practiceId, name: invDetail.practiceName });
            }
         }
       });
     });
     return Array.from(practicesMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Helper function to get unique practices from raw invoice list
  const getUniquePracticesFromInvoices = (invoices) => {
     if (!invoices) return [];
     const practicesMap = new Map();
     invoices.forEach(invoice => {
       if (invoice.status !== 'deleted' && invoice.practiceDetails?.prac_name && invoice.practiceId) {
         if (!practicesMap.has(invoice.practiceId)) {
           practicesMap.set(invoice.practiceId, {
             id: invoice.practiceId,
             name: invoice.practiceDetails.prac_name
           });
         }
       }
     });
     return Array.from(practicesMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };


  // REMOVE fetchPractices function and its useEffect hook
  // const fetchPractices = useCallback(async () => { ... }, []);
  // useEffect(() => { fetchPractices(); }, [auth.currentUser, defaultStartDate, fetchPractices]);


  // Updated fetchInitialData - remove fetchPractices call
  const fetchInitialData = useCallback(async () => {
    if (!auth.currentUser || !defaultStartDate) return;

    setLoading(true);
    try {
      await checkAvailablePeriods();
      await fetchData(); // fetchData now handles practice list population
    } catch (error) {
      console.error('Error in fetchInitialData:', error);
      setData([]);
      setPractices([]); // Clear practices on error
      setAvailablePeriods(['1', '3', '6', 'tax-current', 'tax-previous', 'all-time']);
    }
    setLoading(false);
  }, [auth.currentUser, defaultStartDate, checkAvailablePeriods, fetchData]);

  // Initial data load effect - remove fetchPractices
  useEffect(() => {
    if (auth.currentUser && defaultStartDate) {
      fetchInitialData();
      // fetchPractices(); // Removed
    }
    // Removed fetchPractices from dependency array
  }, [auth.currentUser, defaultStartDate, fetchInitialData]);

  // Refetch data when filters change - No change needed here
  useEffect(() => {
    if (auth.currentUser && defaultStartDate) {
      fetchData();
    }
  }, [filter, practice, customTaxYear, auth.currentUser, defaultStartDate, fetchData]);

  // Refetch initial data when viewMode changes - No change needed here
  useEffect(() => {
    if (auth.currentUser && defaultStartDate) {
      fetchInitialData();
    }
  }, [viewMode, auth.currentUser, defaultStartDate, fetchInitialData]);

  // Remove redundant useEffect hooks that call fetchData/fetchPractices
  // useEffect(() => { if (auth.currentUser) { checkAvailablePeriods(); } }, [auth.currentUser, viewMode]); // Keep checkAvailablePeriods
  // useEffect(() => { fetchData(); }, [filter, practice, auth.currentUser]); // Covered by other effects
  // useEffect(() => { fetchPractices(); }, [auth.currentUser]); // Removed

  // --- Updated useEffect for location.state ---
  useEffect(() => {
    if (location.state) {
      const newViewMode = location.state.viewMode;
      // Practice filter is NOT read from location state anymore to preserve it
      // const newPractice = location.state.practice;

      let needsRefresh = false;

      if (newViewMode && newViewMode !== viewMode) {
        setViewMode(newViewMode);
        needsRefresh = true; // Changing viewMode triggers fetch via its own useEffect
      }

      // REMOVED: Logic that reset practice filter when returning from history
      // if (location.state.returnedFromInvoiceHistory) {
      //   setPractice('all');
      //   needsRefresh = true;
      // }
      // else if (newPractice && newPractice !== practice) {
      //   setPractice(newPractice);
      //   needsRefresh = true;
      // }

      // If only viewMode changed, its useEffect handles the refresh.
      // If no state relevant to this component changed, no explicit refresh needed here.
      // The component simply re-renders with its existing state (including 'practice').

      // Clear location state after processing it
      // Use a slight delay to ensure state updates propagate before clearing
      setTimeout(() => {
         window.history.replaceState(
           { ...window.history.state, usr: {} },
           document.title
         );
      }, 100);
    }
    // Dependencies: only location.state and viewMode (to set it)
    // Practice state is managed internally and shouldn't be reset by this effect.
  }, [location.state, viewMode]);
  // --- End Updated useEffect ---


  // handlePracticeChange - No change needed, just calls fetchData
  const handlePracticeChange = (newPractice) => {
    setPractice(newPractice);
    // fetchData(newPractice); // fetchData is called by the useEffect watching 'practice' state
  };

  // useEffect(() => { ... navigate('/login'); fetchData(); }, [auth.currentUser]); // Keep initial fetch on auth change

  // forceRefreshData - Clear all caches aggressively
  const forceRefreshData = () => {
    console.log('Force refreshing data - clearing all caches...');
    setDataCache({});
    reportCacheUtil.clearAllCache(); // Clear everything in sessionStorage
    setBaseDataTimestamp(null);
    fetchData();
  };

  // handleViewInvoices - Pass current practice filter state
  const handleViewInvoices = (month, practiceName = 'all', selectedInvoices = []) => {
    // practiceName here is just for display in history, use the actual 'practice' state value
    const currentPracticeFilter = practice; // The ID ('all' or specific ID)

    if (selectedInvoices && selectedInvoices.length > 0) {
      const invoiceIds = selectedInvoices.map(invoice => invoice.id);
      const baseUrl = `/invoice-history?month=${encodeURIComponent(month)}&practice=${encodeURIComponent(currentPracticeFilter)}&viewMode=${viewMode}`;
      const idsParam = `&invoiceIds=${invoiceIds.join(',')}`;
      const urlLength = baseUrl.length + idsParam.length;
      if (urlLength > 1800) {
        const storageKey = `temp_invoice_ids_${Date.now()}`;
        localStorage.setItem(storageKey, JSON.stringify(invoiceIds));
        navigate(`${baseUrl}&idStorageKey=${storageKey}`);
      } else {
        navigate(`${baseUrl}${idsParam}`);
      }
    } else {
      navigate(`/invoice-history?month=${encodeURIComponent(month)}&practice=${encodeURIComponent(currentPracticeFilter)}&viewMode=${viewMode}`);
    }
  };


  const getFilterDisplay = () => {
    if (filter === 'all-time') {
      return 'All Time';
    } else if (filter === 'tax-current') {
      const currentYear = getCurrentTaxYear();
      return `Tax Year ${formatTaxYear(currentYear)}`;
    } else if (filter === 'tax-previous') {
      const previousYear = getCurrentTaxYear() - 1;
      return `Tax Year ${formatTaxYear(previousYear)}`;
    } else if (filter === 'tax-custom') {
      const year = customTaxYear || (availableYears[0] || getCurrentTaxYear() - 2);
      return `Tax Year ${formatTaxYear(year)}`;
    } else if (filter === '0') { // Add case for '0'
      return 'This Month';
    } else if (filter === '1') {
      return 'Last Month';
    } else if (filter === '3') {
      return 'Last 3 Months';
    } else if (filter === '6') {
      return 'Last 6 Months';
    } else if (filter === '12') {
      return 'Last 12 Months';
    } else if (filter === '24') {
      return 'Last 24 Months';
    }
    return `Filter: ${filter}`;
  };

  // --- Calculate Summary Totals ---
  const calculateSummaries = (reportData) => {
    if (!reportData || reportData.length === 0) {
      return { totalRevenue: 0, totalInvoicesCount: 0, averageRevenue: 0 };
    }

    // Use parseFloat for robustness when summing amounts
    const totalRevenue = reportData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const totalInvoicesCount = reportData.reduce((sum, item) => sum + item.invoices.length, 0);
    const averageRevenue = totalInvoicesCount > 0 ? totalRevenue / totalInvoicesCount : 0;

    return {
      totalRevenue: totalRevenue,
      totalInvoicesCount: totalInvoicesCount,
      averageRevenue: averageRevenue
    };
  };

  // Calculate summaries based on the current data state
  const summaries = calculateSummaries(data);

  return (
    <div className="container mx-auto p-2 sm:p-4">
      {/* Card: Theme-aware */}
      <Card className={cn(theme === 'light' ? 'bg-white' : 'bg-[#1F1F2E]', 'border-border')}>
        <CardContent className="p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
            <div className="flex flex-col">
              {/* Title & HelpIcon: Theme-aware */}
              <div className="flex items-center gap-2">
                <h2 className={cn("text-xl sm:text-2xl font-bold", theme === 'light' ? 'text-black' : 'text-purple-300')}>Revenue Reports</h2>
                <HelpIcon sectionKey="ReportsPage" /> {/* Use a relevant sectionKey */}
              </div>
              {/* Subtitle & Refresh Button: Theme-aware */}
              <div className={cn("flex items-center mt-1 text-xs", theme === 'light' ? 'text-gray-500' : 'text-purple-300/70')}>
                <span className="mr-2">
                  {baseDataTimestamp ? `Data from ${new Date(baseDataTimestamp).toLocaleString()}` : 'Loading data...'}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={cn(
                    "h-6 px-2 ml-2",
                    theme === 'light' 
                      ? 'text-gray-600 hover:text-black hover:bg-gray-100' 
                      : 'text-purple-300 hover:text-white hover:bg-purple-950/50'
                  )}
                  onClick={forceRefreshData}
                  title="Refresh data from database"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  <span className="text-xs">Refresh</span>
                </Button>
              </div>
            </div>
            {/* Filters: Theme-aware Selects */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className={cn(
                  "w-full sm:w-[200px]",
                  theme === 'light' ? 'bg-white text-black border-gray-300' : 'bg-[#2A2A3E] text-white border-purple-500/20'
                )}>
                  <SelectValue placeholder="View Mode">
                    {viewMode === 'invoiceDate' ? 'By Invoice Date' : 'By Treatment Date'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className={cn(
                  theme === 'light' ? 'bg-white text-black border-gray-300' : 'bg-[#2A2A3E] text-white border-purple-500/20'
                )}>
                  {/* SelectItem should adapt via global styles or add conditional styles if needed */}
                  <SelectItem value="invoiceDate">By Invoice Date</SelectItem>
                  <SelectItem value="treatmentDate">By Treatment Date</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger 
                    className={cn(
                      "w-full sm:w-[200px]",
                      theme === 'light' ? 'bg-white text-black border-gray-300' : 'bg-[#2A2A3E] text-white border-purple-500/20'
                    )}
                    title={`Current filter: ${getFilterDisplay()}`} // Updated title
                  >
                    <SelectValue placeholder="Filter">
                      {getFilterDisplay()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className={cn(
                    theme === 'light' ? 'bg-white text-black border-gray-300' : 'bg-[#2A2A3E] text-white border-purple-500/20'
                  )}>
                    {/* Dynamically render options based on availablePeriods */}
                    {availablePeriods.includes('0') && <SelectItem value="0">This Month</SelectItem>} {/* Add This Month option */}
                    {availablePeriods.includes('1') && <SelectItem value="1">Last Month</SelectItem>}
                    {availablePeriods.includes('3') && <SelectItem value="3">Last 3 Months</SelectItem>}
                    {availablePeriods.includes('6') && <SelectItem value="6">Last 6 Months</SelectItem>}
                    {availablePeriods.includes('12') && <SelectItem value="12">Last 12 Months</SelectItem>}
                    {availablePeriods.includes('24') && <SelectItem value="24">Last 24 Months</SelectItem>}
                    {availablePeriods.includes('all-time') && <SelectItem value="all-time">All Time</SelectItem>}
                    
                    {/* Render Tax Year options if available */}
                    {(availablePeriods.includes('tax-current') || availablePeriods.includes('tax-previous')) && (
                       <SelectItem value="divider" disabled className="py-2 opacity-50 text-xs font-semibold">
                         TAX YEAR PERIODS
                       </SelectItem>
                    )}
                    {availablePeriods.includes('tax-current') && (
                       <SelectItem value="tax-current">
                         Current Tax Year ({formatTaxYear(getCurrentTaxYear())})
                       </SelectItem>
                    )}
                    {availablePeriods.includes('tax-previous') && (
                       <SelectItem value="tax-previous">
                         Previous Tax Year ({formatTaxYear(getCurrentTaxYear() - 1)})
                       </SelectItem>
                    )}
                    {/* Add Custom Tax Year option if needed */}
                    {/* {availableYears.length > 2 && <SelectItem value="tax-custom">Custom Tax Year...</SelectItem>} */}
                  </SelectContent>
                </Select>
              </div>
              <Select value={practice} onValueChange={handlePracticeChange}>
                <SelectTrigger className={cn(
                  "w-full sm:w-[200px]",
                  theme === 'light' ? 'bg-white text-black border-gray-300' : 'bg-[#2A2A3E] text-white border-purple-500/20'
                )}>
                  <SelectValue placeholder="Practice">
                    {/* Display name based on current 'practice' state and 'practices' list */}
                    {practice === 'all' ? 'All Practices' : practices.find(p => p.id === practice)?.name || 'Select Practice'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className={cn(
                  theme === 'light' ? 'bg-white text-black border-gray-300' : 'bg-[#2A2A3E] text-white border-purple-500/20'
                )}>
                  <SelectItem value="all">All Practices</SelectItem>
                  {/* Map over the dynamically updated 'practices' state */}
                  {practices.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Tab Buttons: Theme-aware */}
          <div className="flex space-x-2 mb-4">
            <Button
              variant={activeChartTab === 'monthlyRevenue' ? 'default' : 'ghost'}
              className={cn(
                activeChartTab === 'monthlyRevenue' 
                  ? 'bg-purple-700 text-white' // Active state consistent
                  : theme === 'light' 
                    ? 'text-gray-600 hover:bg-gray-100 hover:text-black' // Inactive light
                    : 'text-purple-300 hover:bg-purple-800/50' // Inactive dark
              )}
              onClick={() => setActiveChartTab('monthlyRevenue')}
            >
              Monthly Revenue
            </Button>
            <Button
              variant={activeChartTab === 'practiceBreakdown' ? 'default' : 'ghost'}
              className={cn(
                activeChartTab === 'practiceBreakdown' 
                  ? 'bg-purple-700 text-white' // Active state consistent
                  : theme === 'light' 
                    ? 'text-gray-600 hover:bg-gray-100 hover:text-black' // Inactive light
                    : 'text-purple-300 hover:bg-purple-800/50' // Inactive dark
              )}
              onClick={() => setActiveChartTab('practiceBreakdown')}
            >
              Practice Breakdown
            </Button>
          </div>
          {loading ? (
            // Loading Message: Theme-aware
            <div className="text-center py-10">
              <span className={cn(theme === 'light' ? 'text-gray-500' : 'text-purple-300')}>Loading data...</span>
            </div>
          ) : data.length > 0 ? (
            <>
              <div className="mb-4 sm:mb-8">
                <RevenueGraph
                  data={data}
                  title={`Revenue ${viewMode === 'invoiceDate' ? 'by Invoice Date' : 'by Treatment Date'}`}
                  chartMode={activeChartTab}
                  theme={theme} // Pass theme to graph
                />
              </div>
              {/* Summary Boxes: Use calculated summaries */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4">
                <div className={cn("p-4 rounded-lg flex flex-col", theme === 'light' ? 'bg-gray-100' : 'bg-[#2A2A3E]')}>
                  <h3 className={cn("text-sm font-medium mb-2 text-center", theme === 'light' ? 'text-gray-700' : 'text-purple-300')}>Total Revenue</h3>
                  <p className={cn("text-2xl font-bold text-center mt-auto", theme === 'light' ? 'text-black' : 'text-white')}>
                    £{summaries.totalRevenue.toFixed(2)} {/* Use calculated value */}
                  </p>
                </div>
                <div className={cn("p-4 rounded-lg flex flex-col", theme === 'light' ? 'bg-gray-100' : 'bg-[#2A2A3E]')}>
                  <h3 className={cn("text-sm font-medium mb-2 text-center", theme === 'light' ? 'text-gray-700' : 'text-purple-300')}>Total Invoices</h3>
                  <p className={cn("text-2xl font-bold text-center mt-auto", theme === 'light' ? 'text-black' : 'text-white')}>
                    {summaries.totalInvoicesCount} {/* Use calculated value */}
                  </p>
                </div>
                <div className={cn("p-4 rounded-lg flex flex-col", theme === 'light' ? 'bg-gray-100' : 'bg-[#2A2A3E]')}>
                  <h3 className={cn("text-sm font-medium mb-2 text-center", theme === 'light' ? 'text-gray-700' : 'text-purple-300')}>Average per Invoice</h3>
                  <p className={cn("text-2xl font-bold text-center mt-auto", theme === 'light' ? 'text-black' : 'text-white')}>
                    £{summaries.averageRevenue.toFixed(2)} {/* Use calculated value */}
                  </p>
                </div>
              </div>
              {/* Detailed Data Table: Theme-aware */}
              <div className="mt-4 sm:mt-8 overflow-x-auto">
                <h3 className={cn("text-lg font-medium mb-4", theme === 'light' ? 'text-black' : 'text-purple-300')}>Detailed Data</h3>
                <div className="min-w-full inline-block align-middle">
                  <div className={cn("overflow-x-auto border rounded-lg", theme === 'light' ? 'border-gray-300' : 'border-purple-500/20')}>
                    <table className={cn("min-w-full divide-y", theme === 'light' ? 'divide-gray-300' : 'divide-purple-500/20')}>
                      <thead className={cn(theme === 'light' ? 'bg-gray-100' : 'bg-[#2A2A3E]')}>
                        <tr>
                          <th className={cn("py-2 px-3 sm:px-4 text-left text-xs sm:text-sm font-semibold", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>
                            Month
                          </th>
                          <th className={cn("py-2 px-3 sm:px-4 text-right text-xs sm:text-sm font-semibold", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>
                            Revenue
                          </th>
                          <th className={cn("py-2 px-3 sm:px-4 text-right text-xs sm:text-sm font-semibold", theme === 'light' ? 'text-gray-600' : 'text-purple-300')}>
                            Invoices
                          </th>
                        </tr>
                      </thead>
                      <tbody className={cn("divide-y", theme === 'light' ? 'divide-gray-300' : 'divide-purple-500/20')}>
                        {data.map((item, index) => (
                          <tr key={index} className={cn(
                            theme === 'light' 
                              ? (index % 2 === 0 ? 'bg-white' : 'bg-gray-50') 
                              : (index % 2 === 0 ? 'bg-[#1F1F2E]' : 'bg-[#2A2A3E]')
                          )}>
                            <td className={cn("py-2 px-3 sm:px-4 text-left text-xs sm:text-sm whitespace-nowrap", theme === 'light' ? 'text-black' : 'text-white')}>
                              {item.month}
                            </td>
                            <td className={cn("py-2 px-3 sm:px-4 text-right text-xs sm:text-sm whitespace-nowrap", theme === 'light' ? 'text-black' : 'text-white')}>
                              {/* Ensure amount is treated as number */}
                              £{item.amount ? parseFloat(item.amount).toFixed(2) : '0.00'}
                            </td>
                            <td className="py-2 px-3 sm:px-4 text-right text-xs sm:text-sm whitespace-nowrap">
                              <button 
                                onClick={() => handleViewInvoices(item.month, practice === 'all' ? 'all' : practices.find(p => p.id === practice)?.name, item.invoiceDetails)}
                                className={cn(
                                  "hover:underline",
                                  theme === 'light' ? 'text-blue-600 hover:text-blue-800' : 'text-purple-300 hover:text-purple-200'
                                )}
                              >
                                {item.invoices.length}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          ) : (
            // No Data Message: Theme-aware
            <div className={cn("text-center py-10", theme === 'light' ? 'text-gray-500' : 'text-purple-300')}>No revenue data available</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
